import { formatMoneyRs } from "../../../utils/money";
import { FC, FormEvent, useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import QRCode from "qrcode";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { getAuth } from "../../auth";
import { getUserByToken } from "../../auth/core/_requests";
import { getLoggedUserIdentity } from "../../../utils/loggedUser";
import {
  createCardapioPedido,
  createSicrediPixPayment,
  fetchSicrediPixStatus,
  updateCardapioPedidosStatus,
  CARDAPIO_PEDIDO_STATUS,
} from "../utils/cardapio.api";
import {
  createTransaction,
  fetchOpenSession,
  type CaixaSession,
} from "../../caixa-financeiro/utils/caixa.api";
import { formatCurrency } from "../utils/cardapio.utils";
import { PaymentMethod } from "../../caixa-financeiro/utils/caixa.utils";

interface CartLine {
  _id_cardapio_item: number;
  item_name: string;
  quantity: number;
  unit_price: number;
}

interface Props {
  show: boolean;
  onHide: () => void;
  amount: number;
  waiterId: number;
  waiterName: string;
  wristband: string;
  commissionPercent?: number;
  cart: CartLine[];
  pedidoId?: number;
  pedidoIds?: number[];
  /** Código do cliente em companies-users.usuarios */
  _id_usuario?: number | null;
  initialMethod?: PaymentMethod;
  onPaymentSuccess?: () => void;
}

type ModalStep = "method" | "details" | "pix";

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string; icon: string }[] = [
  { value: "dinheiro", label: "Dinheiro", icon: "💵" },
  { value: "pix", label: "PIX", icon: "📱" },
  { value: "debito", label: "Débito", icon: "💳" },
  { value: "credito", label: "Crédito", icon: "💳" },
];

function formatValorPtBR(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function getVencimentoBr(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  return `${dia}/${mes}/${d.getFullYear()}`;
}

const CAIXA_REQUIRED_MSG =
  "Abra o caixa em Caixa Financeiro (/caixa-financeiro) para registrar pagamentos.";

export const CardapioPedidoPaymentModal: FC<Props> = ({
  show,
  onHide,
  amount,
  waiterId,
  waiterName,
  wristband,
  commissionPercent,
  cart,
  pedidoId,
  pedidoIds,
  _id_usuario,
  initialMethod,
  onPaymentSuccess,
}) => {
  const [openSession, setOpenSession] = useState<CaixaSession | null>(null);

  const [step, setStep] = useState<ModalStep>("method");
  const [method, setMethod] = useState<PaymentMethod | "">("");
  const [processing, setProcessing] = useState(false);
  const [valorRecebido, setValorRecebido] = useState(0);
  const [cardBrand, setCardBrand] = useState("");
  const [authorizationCode, setAuthorizationCode] = useState("");

  const [pixQrDataUrl, setPixQrDataUrl] = useState("");
  const [pixCopiaECola, setPixCopiaECola] = useState("");
  const [pixTxid, setPixTxid] = useState("");
  const [pixExpiresAt, setPixExpiresAt] = useState<Date | null>(null);
  const [pixStatus, setPixStatus] = useState<"pending" | "paid" | "error">("pending");
  const [countdown, setCountdown] = useState("");
  const [copied, setCopied] = useState(false);

  const pollingRef = useRef<NodeJS.Timer | null>(null);
  const valorRecebidoInputRef = useRef<HTMLInputElement>(null);

  const troco =
    method === "dinheiro" && valorRecebido >= amount ? valorRecebido - amount : 0;

  const resetState = () => {
    setStep("method");
    setMethod("");
    setProcessing(false);
    setValorRecebido(0);
    setCardBrand("");
    setAuthorizationCode("");
    setPixQrDataUrl("");
    setPixCopiaECola("");
    setPixTxid("");
    setPixExpiresAt(null);
    setPixStatus("pending");
    setCountdown("");
    setCopied(false);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  useEffect(() => {
    if (!show) return;
    fetchOpenSession()
      .then((session) => setOpenSession(session?.status === "aberto" ? session : null))
      .catch(() => setOpenSession(null));
  }, [show]);

  useEffect(() => {
    if (!show) resetState();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [show]);

  useEffect(() => {
    if (!pixExpiresAt || pixStatus !== "pending") return;
    const tick = () => {
      const diff = pixExpiresAt.getTime() - Date.now();
      if (diff <= 0) {
        setCountdown("Expirado");
        return;
      }
      const min = Math.floor(diff / 60000);
      const sec = Math.floor((diff % 60000) / 1000);
      setCountdown(`${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [pixExpiresAt, pixStatus]);

  const handleValorRecebidoKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    const isDigit = /^[0-9]$/.test(key);
    const isBackspace = key === "Backspace";
    if (!isDigit && !isBackspace) return;

    e.preventDefault();
    const valueCents = Math.round(valorRecebido * 100);

    if (isDigit) {
      const digit = parseInt(key, 10);
      const newCents = Math.min(valueCents * 10 + digit, 999999999999);
      setValorRecebido(newCents / 100);
    } else {
      setValorRecebido(Math.floor(valueCents / 10) / 100);
    }

    requestAnimationFrame(() => {
      const len = valorRecebidoInputRef.current?.value?.length ?? 0;
      valorRecebidoInputRef.current?.setSelectionRange(len, len);
    });
  };

  const markPedidosAsPago = async (ids: number[]) => {
    const unique = Array.from(new Set(ids.map(Number).filter((id) => id > 0)));
    if (!unique.length) return;
    try {
      await updateCardapioPedidosStatus(unique, CARDAPIO_PEDIDO_STATUS.PAGO);
    } catch {
      // pagamento no caixa já registrado; status pode falhar sem bloquear UX
    }
  };

  const registerPedido = async () => {
    const existingIds = [
      ...(pedidoIds || []),
      ...(pedidoId ? [pedidoId] : []),
    ].filter((id) => Number(id) > 0);
    if (existingIds.length) {
      return { _id: existingIds[0], ids: existingIds };
    }
    const created = await createCardapioPedido({
      waiter_name: waiterName,
      seller_name: waiterName,
      wristband_number: wristband.trim(),
      commission_percent: commissionPercent,
      _id_usuario: _id_usuario || undefined,
      cliente_id: _id_usuario || undefined,
      status: CARDAPIO_PEDIDO_STATUS.PAGO,
      items: cart.map((l) => ({
        _id_cardapio_item: l._id_cardapio_item,
        quantity: l.quantity,
      })),
    });
    return { _id: created._id, ids: [created._id] };
  };

  // Dinheiro e cartão. O PIX é registrado no caixa pelo servidor (AAC-113).
  const registerCaixaTransaction = async (paymentMethod: PaymentMethod) => {
    const session = await fetchOpenSession();
    if (!session) {
      throw new Error("Nenhuma sessão de caixa aberta.");
    }

    const itemsSummary = cart.map((l) => l.item_name).join(", ");

    await createTransaction({
      _id_session: session._id,
      _id_employee: waiterId,
      client_name: `Pulseira ${wristband}`,
      sector_description: "Cardápio",
      items: cart.map((l) => ({
        barcode: `cardapio-${l._id_cardapio_item}`,
        product_name: l.item_name,
        sector: "Cardápio",
        unit_price: l.unit_price,
        quantity: l.quantity,
      })),
      payments: [
        {
          payment_method: paymentMethod,
          amount,
          ...(paymentMethod === "credito" || paymentMethod === "debito"
            ? {
                card_brand: cardBrand.trim() || undefined,
                authorization_code: authorizationCode.trim() || undefined,
              }
            : {}),
        },
      ],
    });

    void itemsSummary;
  };

  // AAC-113: a baixa (caixa e pedidos) é do servidor, que também a faz pelo webhook do Sicredi e
  // pela conciliação a cada minuto; o painel só exibe. Fechar o modal não perde o pagamento.
  const startPixPolling = (txid: string) => {
    pollingRef.current = setInterval(() => {
      fetchSicrediPixStatus(txid)
        .then((res) => {
          const status = res?.status ?? res?.resposta_sicredi?.status;
          const paid =
            res?.paid === true || status === "CONCLUIDA" || status === "RECEIVED";
          if (paid) {
            setPixStatus("paid");
            if (pollingRef.current) clearInterval(pollingRef.current);
            pollingRef.current = null;
            Swal.fire("Sucesso", "Pagamento PIX confirmado!", "success");
            onPaymentSuccess?.();
          }
        })
        .catch(() => {});
    }, 5000);
  };

  const processPixPayment = async (pedidoId: number, idsToMark: number[]) => {
    const session = await fetchOpenSession();
    if (!session?._id || session.status !== "aberto") {
      throw new Error(CAIXA_REQUIRED_MSG);
    }

    const tokenData = getAuth();
    if (!tokenData?.api_token) throw new Error("Sessão expirada.");

    const userRes = await getUserByToken(tokenData.api_token);
    const userId = String(userRes.data.id ?? "1");
    const itemsSummary = cart.map((l) => `${l.quantity}x ${l.item_name}`).join(", ");

    const response = await createSicrediPixPayment({
      id: {
        usuario: userId,
        companhia: "Cardápio",
        setor: "Garçom",
        cliente: waiterName,
        pulseira: wristband,
        item: itemsSummary.slice(0, 120),
      },
      pagamento: {
        valor: amount,
        multa: 0,
        juros: 0,
        vencimento: getVencimentoBr(),
      },
      pix: {
        id_dispositivo: "cardapio-pedido",
        timeout: 3600,
        limite: 50000,
        parcelamento: false,
      },
      dispositivo: {
        nome: "Cardápio Garçom",
        modelo: "Web",
        serial: `PED-${pedidoId}`,
        versao: "1.0.0",
        bateria: "100%",
        ip: "127.0.0.1",
        status: "online",
        ultimo_acesso: new Date().toISOString(),
      },
      local: {
        cidade: "Local",
        endereco: "Cardápio / Pedido",
      },
      caixa_sessao_id: session._id,
      cardapio_pedido_ids: idsToMark
        .map(Number)
        .filter((id) => Number.isInteger(id) && id > 0),
      ...(Number(waiterId) > 0 ? { funcionario_id: Number(waiterId) } : {}),
    });

    const copiaECola =
      response.pix_copia_e_cola ||
      response.resposta_sicredi?.pixCopiaECola ||
      "";
    const txid =
      response.txid || response.resposta_sicredi?.txid || "";

    if (!copiaECola) throw new Error("Resposta PIX sem código copia e cola.");

    const expiracaoSegundos =
      Number(response.resposta_sicredi?.calendario?.expiracao) || 3600;
    const expiresAt = new Date(Date.now() + expiracaoSegundos * 1000);

    const qrDataUrl = await QRCode.toDataURL(copiaECola, { errorCorrectionLevel: "M" });

    setPixCopiaECola(copiaECola);
    setPixQrDataUrl(qrDataUrl);
    setPixTxid(txid);
    setPixExpiresAt(expiresAt);
    setPixStatus("pending");
    setStep("pix");

    if (txid) startPixPolling(txid);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!method) {
      toast.warning("Selecione uma forma de pagamento.");
      return;
    }
    if (method === "dinheiro" && valorRecebido < amount) {
      toast.warning("O valor recebido deve ser maior ou igual ao total.");
      return;
    }
    if (method === "pix" && !openSession?._id) {
      toast.error(CAIXA_REQUIRED_MSG);
      return;
    }

    setProcessing(true);
    try {
      const pedido = await registerPedido();

      if (method === "pix") {
        await processPixPayment(pedido._id, pedido.ids);
        return;
      }

      await registerCaixaTransaction(method);
      await markPedidosAsPago(pedido.ids);
      Swal.fire("Sucesso", "Pagamento registrado com sucesso!", "success");
      onPaymentSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string; error?: string } } })
          .response?.data?.message ||
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
        (err as { message?: string }).message ||
        "Não foi possível processar o pagamento.";
      Swal.fire("Erro", message, "error");
    } finally {
      setProcessing(false);
    }
  };

  const selectMethod = (value: PaymentMethod) => {
    setMethod(value);
    setStep("details");
  };

  return (
    <>
      <Modal show={show && step !== "pix"} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {step === "method" ? "Forma de Pagamento" : "Confirmar Pagamento"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4 p-3 bg-light rounded">
            <div className="text-muted fs-7">Total do pedido</div>
            <div className="fs-2 fw-bold text-primary">{formatCurrency(amount)}</div>
            <div className="text-muted fs-8 mt-1">
              Garçom: {waiterName} · Pulseira: {wristband}
            </div>
          </div>

          {step === "method" && (
            <div className="row g-3">
              {PAYMENT_OPTIONS.map((opt) => (
                <div key={opt.value} className="col-6">
                  <button
                    type="button"
                    className={`btn w-100 py-4 d-flex flex-column align-items-center gap-2 border border-2 ${
                      method === opt.value
                        ? "btn-primary border-primary"
                        : "btn-light border-gray-300"
                    }`}
                    onClick={() => selectMethod(opt.value)}
                  >
                    <span className="fs-1">{opt.icon}</span>
                    <span className="fw-semibold">{opt.label}</span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {step === "details" && method && (
            <Form onSubmit={handleSubmit}>
              <div className="mb-4">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setStep("method")}
                >
                  ← Trocar forma de pagamento
                </button>
                <span className="ms-3 badge badge-light-primary fs-7">
                  {PAYMENT_OPTIONS.find((o) => o.value === method)?.label}
                </span>
              </div>

              {method === "dinheiro" && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Valor recebido</Form.Label>
                    <Form.Control
                      ref={valorRecebidoInputRef}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="R$ 0,00"
                      className="text-end fs-4 fw-bold"
                      value={formatValorPtBR(valorRecebido)}
                      onKeyDown={handleValorRecebidoKeyDown}
                      onChange={() => {}}
                      required
                    />
                  </Form.Group>
                  {valorRecebido >= amount && (
                    <div className="p-3 rounded border border-success bg-success bg-opacity-10 mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <strong>Troco:</strong>
                        <span className="fs-4 fw-bold text-success">
                          {formatValorPtBR(troco)}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {(method === "debito" || method === "credito") && (
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <Form.Label>Bandeira (opcional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ex.: Visa, Master"
                      value={cardBrand}
                      onChange={(e) => setCardBrand(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Autorização (opcional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Código NSU / autorização"
                      value={authorizationCode}
                      onChange={(e) => setAuthorizationCode(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {method === "pix" && (
                <div className="alert alert-info d-flex align-items-center gap-2">
                  <span>📱</span>
                  <span>
                    Ao confirmar, será gerado um QR Code PIX via Sicredi para pagamento
                    instantâneo.
                  </span>
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="light" onClick={handleClose} disabled={processing}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={processing}>
                  {processing ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Processando...
                    </>
                  ) : method === "pix" ? (
                    "Gerar QR Code PIX"
                  ) : (
                    "Confirmar Pagamento"
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={show && step === "pix"}
        onHide={handleClose}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Pagamento via PIX</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {pixStatus === "paid" ? (
            <div className="py-5">
              <div className="text-success mb-3" style={{ fontSize: "4rem" }}>
                ✓
              </div>
              <h4 className="text-success">Pagamento confirmado!</h4>
              <p className="text-muted">O PIX foi recebido com sucesso.</p>
              <Button
                variant="primary"
                onClick={() => {
                  handleClose();
                }}
              >
                Fechar
              </Button>
            </div>
          ) : (
            <>
              <p className="text-muted mb-1">
                Escaneie o QR Code ou copie o código abaixo no app do seu banco.
              </p>
              <div className="fs-3 fw-bold text-primary mb-3">{formatCurrency(amount)}</div>

              {pixQrDataUrl && (
                <img
                  src={pixQrDataUrl}
                  alt="QR Code PIX para pagamento"
                  className="border rounded p-2 bg-white mb-3"
                  style={{ maxWidth: 260, width: "100%" }}
                />
              )}

              {countdown && (
                <div className="mb-3">
                  <span className="badge badge-light-warning fs-7">
                    Expira em: {countdown}
                  </span>
                </div>
              )}

              <div className="input-group mb-3">
                <Form.Control
                  as="textarea"
                  rows={3}
                  readOnly
                  value={pixCopiaECola}
                  className="font-monospace fs-8"
                  aria-label="Código PIX copia e cola"
                />
                <CopyToClipboard
                  text={pixCopiaECola}
                  onCopy={() => {
                    setCopied(true);
                    toast.success("Código PIX copiado!");
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  <Button variant={copied ? "success" : "outline-primary"}>
                    {copied ? "Copiado!" : "Copiar"}
                  </Button>
                </CopyToClipboard>
              </div>

              <div className="d-flex align-items-center justify-content-center gap-2 text-muted">
                <Spinner animation="border" size="sm" />
                <span>
                  Aguardando confirmação do pagamento... Pode fechar esta janela: o pedido é
                  baixado automaticamente quando o PIX cair.
                </span>
              </div>

              {pixTxid && (
                <div className="text-muted fs-8 mt-3">TXID: {pixTxid}</div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
